import moment from 'moment'
import { classNames } from 'primereact/utils'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { bindActionCreators } from 'redux'

// local imports
import { actionCreators } from '../../../state/state-index'
import { PlanTypes, ReservationStatuses } from '../../../types/BaseType'
import { ICalendarPlan } from '../../../types/CalendarType'
import { ICalendarSquads, IEmployeeSummary } from '../../../types/Employee'

/* Assets */
import lactation from 'assets/icons/baby.svg'
import doctor from 'assets/icons/doctor.svg'
import gym from 'assets/icons/gym.svg'
import hair from 'assets/icons/hairdresser.svg'
import meeting from 'assets/icons/meeting-room.svg'
import park from 'assets/icons/park-area.svg'
import poolVehicle from 'assets/icons/pool-vehicle.svg'

const Day = (props: any) => {
  const {
    day,
    day: { date, isCurrentMonth, isToday, number, isVisible, isHolidayColor, txtDay, visibleHolidayText },
    event,
    select,
    events,
    squadsEvents,
    showSquadsEvent,
  } = props
  const { t } = useTranslation()
  let [selectedEvent, setSelectedEvent] = useState()
  let [availableClass, setAvailableClass] = useState('')
  const dispatch = useDispatch()
  const { changeDayState } = bindActionCreators(actionCreators, dispatch)

  useEffect(() => {
    setAvailableClass(getClassFromEvent())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getSelectedEvent = (): any => {
    // Eğer prop olarak event geldiyse ve içi doluysa onu kullan
    if (event && Object.keys(event).length > 0) {
      return event
    }
    
    // Yoksa eski mantıkla ara (backward compatibility)
    if (events === null || events === undefined || events.length === 0) return null
    let compDate = day.date
    let compDateFormat = compDate.format('YYYY-MM-DD')
    let selectedEvent = null
    events.forEach((el: { reservationDate: any }) => {
      let startDateFormat = moment(el.reservationDate).format('YYYY-MM-DD')
      if (compDateFormat === startDateFormat) {
        selectedEvent = el
      }
    })
    return selectedEvent
  }

  const getSelectedSquadsEvent = (): any => {
    if (squadsEvents === null || squadsEvents === undefined || squadsEvents.length === 0) return null
    let compDate = day.date
    let compDateFormat = compDate.format('YYYY-MM-DD')
    let selectedEvents: { Employee: IEmployeeSummary; Event: ICalendarPlan }[] = []
    squadsEvents.forEach((events: ICalendarSquads) => {
      events?.events.forEach((event: ICalendarPlan) => {
        let startDateFormat = moment(event.reservationDate).format('YYYY-MM-DD')
        if (compDateFormat === startDateFormat) {
          selectedEvents.push({ Employee: events?.info, Event: event })
        }
      })
    })
    return selectedEvents
  }

  const getSquadsEventType = (): any => {
    if (squadsEvents === null || squadsEvents === undefined || squadsEvents.length === 0) return null
    return squadsEvents[0].type
  }

  const getClassFromEvent = () => {
    let availableEvent = showSquadsEvent ? getSelectedSquadsEvent() : getSelectedEvent()
    let availableEventType = showSquadsEvent && getSquadsEventType()

    if (!availableEvent) return ''
    setSelectedEvent(availableEvent)
    let { planType: PlanType, reservationStatusId: ReservationStatusId } = availableEvent

    if (availableEvent.length > 0 && availableEventType === 'all') return ' squads-all'
    if (availableEvent.length > 0 && availableEventType === 'office') return ' squads-office'
    if (availableEvent.length > 0 && availableEventType === 'commonOffice') return ' squads-commonOffice'
    if (availableEvent.length > 0 && availableEventType === 'remote') return ' squads-remote'

    if (PlanType === 0) return ''
    else if (PlanType === 7) return ' holiday'
    else if (PlanType === 20 || PlanType === 21) return ' leave-report'
    else if (PlanType === 999) return ' campus'
    else if (PlanType === PlanTypes.HomeOffice && ReservationStatusId === ReservationStatuses.Approved)
      return ' home-plan'
    else if (PlanType === PlanTypes.HomeOffice && ReservationStatusId === ReservationStatuses.PendingApproval)
      return ' home-plan-request'
    else if (PlanType === PlanTypes.TableReservation) return ' office-plan'
    else if (PlanType === PlanTypes.TableFloorReservation) return ' office-plan'
    else if (PlanType === PlanTypes.OtherLocationTableReservation) return ' common-plan'
    else if (PlanType === PlanTypes.OtherLocationTableFloorReservation) return ' common-plan'
    else return ''
  }

  const getOnCampusIcon = (event: ICalendarPlan) => {
    const hasDocAppointment = !!event.doctorReservation?.length
    const hasCoiffeurReservation = !!event.coiffeurReservation?.length
    const hasLactationReservation = !!event.lactationReservation?.length
    const hasMeetingReservation = !!event.meetingRoomReservation?.length
    const hasParkAreaReservation = !!event.parkAreaReservation?.length
    const hasPoolVehicleReservation = !!event.poolVehicleReservation?.length
    const hasGymReservation =
      !!event.gymGroupLessonReservation?.length ||
      !!event.gymSaloonReservation?.length ||
      !!event.gymTrainerReservation?.length

    if (
      !hasCoiffeurReservation &&
      !hasDocAppointment &&
      !hasGymReservation &&
      !hasLactationReservation &&
      !hasParkAreaReservation &&
      !hasMeetingReservation &&
      !hasPoolVehicleReservation
    )
      return null

    return (
      <div className="d-flex gap-1 w-100">
        {hasDocAppointment && <img src={doctor} alt="health-services" />}
        {hasCoiffeurReservation && <img src={hair} alt="coiffeur-services" />}
        {hasGymReservation && <img src={gym} alt="gym-services" />}
        {hasLactationReservation && <img src={lactation} alt="lactation-room-services" />}
        {hasParkAreaReservation && <img src={park} alt="park-area-services" />}
        {hasMeetingReservation && <img src={meeting} alt="meeting-room-services" />}
        {hasPoolVehicleReservation && <img src={poolVehicle} alt="pool-vehicle-services" />}
      </div>
    )
  }

  const getDescription = (event: ICalendarPlan) => {
    if (!(event && Object.keys(event).length === 0 && Object.getPrototypeOf(event) === Object.prototype)) {
      let { planType, reservationStatusId } = event
      if (planType === 0) return ''
      else if (planType === 20 || planType === 21) return t('calendar.leave-report')
      else if (planType === PlanTypes.HomeOffice && reservationStatusId === ReservationStatuses.Approved)
        return `${event.description}`
      else if (planType === PlanTypes.HomeOffice && reservationStatusId === ReservationStatuses.PendingApproval)
        return `Onay Bekliyor / ${event.description}`
      else if (planType === PlanTypes.TableReservation)
        return `${event?.floorName}, ${event?.blockName}, ${event.districtName}`
      else if (planType === PlanTypes.TableFloorReservation)
        return `${event?.floorName}, ${event?.blockName}, ${event.districtName}`
      else if (planType === PlanTypes.OtherLocationTableReservation)
        return `${event.locationFirmName}, ${event.districtName}`
      else if (planType === PlanTypes.OtherLocationTableFloorReservation)
        return `${event.locationFirmName}, ${event.districtName}`
      else return ''
    }
  }
  const getRemoteOfficeType = (event: ICalendarPlan) => {
    if (!(event && Object.keys(event).length === 0 && Object.getPrototypeOf(event) === Object.prototype)) {
      let { planType } = event
      if (planType === PlanTypes.HomeOffice) {
        return event.remoteOfficeType !== null && event.remoteOfficeType !== '' && ` ${event.remoteOfficeType}`
      }
    }
  }
  const getSquadsDescription = (squadsDescriptions: { Employee: IEmployeeSummary; Event: ICalendarPlan }[]) => {
    if (squadsDescriptions.length > 0) {
      const squadsDescription: JSX.Element[] = squadsDescriptions.map((description) => {
        return (
          <div className={'squads-name'}>
            {description.Employee.name.replace(/(.{7})..+/, '$1…') +
              ' ' +
              description.Employee.surname.charAt(0) +
              '...'}
          </div>
        )
      })
      return squadsDescription
    }
  }

  const getSquadsDots = (squadsDescriptions: { Employee: IEmployeeSummary; Event: ICalendarPlan }[]) => {
    if (squadsDescriptions.length > 0) {
      return <div className={'day-dots'}></div>
    }
  }

  const handleDayClick = () => {
    console.log('DAY handleDayClick CALLED', { number, isVisible, availableClass, selectedEvent })
    // @ts-ignore
    const isEmptySquadsCalendar = selectedEvent !== undefined && selectedEvent?.length === 0
    if (isEmptySquadsCalendar) return

    select({ day: day, event: selectedEvent })
  }

  const onChangeEvent = () => {
    console.log('DAY onChangeEvent CALLED', { number, isVisible, selectedEvent })
    if (!isVisible) return
    day.isChecked = !day.isChecked
    changeDayState({ day: day, event: selectedEvent === undefined ? {} : selectedEvent })
  }

  return (
    <>
      <div
        onClick={onChangeEvent}
        key={date.toString()}
        className={classNames(
          'day',
          !isVisible ? 'disabled' : '',
          !isCurrentMonth ? 'different-month' : '',
          isToday ? 'today' : '',
          day.isChecked ? 'selected' : ''
        )}>
        <div className="d-flex flex-column gap-1">
          <div className={classNames('day-event-icon', availableClass, !isHolidayColor && 'holiday')} />
        </div>

        <div
          className="day-info"
          style={!isVisible || availableClass.length === 0 ? { pointerEvents: 'none' } : {}}>
          <span className="day-num">{number}</span>

          {/*  Holiday Text */}
          {visibleHolidayText ? (
            <p className="plan-description">{txtDay}</p>
          ) : (
            <>
              {!!selectedEvent ? (
                <p className="plan-description">
                  {/* Event Description */}
                  {showSquadsEvent ? getSquadsDescription(selectedEvent) : getDescription(selectedEvent)}
                  <strong className="plan-description-strong">
                    {' '}
                    {selectedEvent && (showSquadsEvent ? '' : getRemoteOfficeType(selectedEvent))}
                  </strong>
                </p>
              ) : null}
            </>
          )}

          <div className="on-campus-icon">{selectedEvent && getOnCampusIcon(selectedEvent)}</div>
        </div>

        {selectedEvent && (showSquadsEvent ? getSquadsDots(selectedEvent) : '')}
      </div>
    </>
  )
}

export default Day
